import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, message } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { StyledChooseWinnerRandomlyButton, StyledSearchInput, StyledUsersContainer } from './StyledUsers';
import { UserList } from '../UserList';
import { TourTrashStatusType, TourTrashType, TourTrashUserType } from '../../../../types/tourtrash';
import {
  PickTourtrashWinnerPayload,
  postTourTrashPickRandomWinner,
  postTourTrashPickWinner,
} from '../../../../repository/mytm/tour-trash/tourTrashRepository';
import { PER_PAGE_DEFAULT } from '../../../../constants/common';
import { TOUR_TRASH_TRANSLATION } from '../../../../constants/define/translation';
import RandomIcon from '../../../../assets/images/IconRandom.png';
import useDebounce from '../../../../hooks/debounced';

interface UsersProps {
  tourTrash: TourTrashType;
  onRefresh: () => void;
  onUpdateEditingRecord: (tourtrash: TourTrashType) => void;
  disableTab: boolean;
  onClose: () => void;
}

export const UsersTab = (props: UsersProps) => {
  const { t } = useTranslation(TOUR_TRASH_TRANSLATION);
  const { tourTrash, onRefresh, onUpdateEditingRecord, disableTab, onClose } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_DEFAULT);
  const [isPickingWinner, setIsPickingWinner] = useState(false);
  const [isPickingRandomWinner, setIsPickingRandomWinner] = useState(false);
  const debouncedSearch = useDebounce(search, 500);
  const [selectedRowId, setSelectedRowId] = useState<string>();

  const handlePickWinner = useCallback(
    (values: PickTourtrashWinnerPayload) => {
      setIsPickingWinner(true);
      postTourTrashPickWinner(values)
        .then(res => {
          setIsPickingWinner(false);
          if (res.data.winner) {
            onClose();
            setTimeout(() => {
              onUpdateEditingRecord({ ...tourTrash, winner: res.data.winner });
              onRefresh();
              message.success(t('message.winner.success'));
            });
          }
        })
        .catch(e => {
          message.error(e.errorMessage || t('message.winner.fail'));
          setIsPickingWinner(false);
        });
    },
    [onUpdateEditingRecord, onRefresh, onClose],
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handlePickRandomWinner = useCallback(() => {
    setIsPickingRandomWinner(true);
    postTourTrashPickRandomWinner({ perPage: PER_PAGE_DEFAULT, tourTrashId: tourTrash?.id })
      .then(res => {
        setIsPickingRandomWinner(false);
        if (res.data.winner) {
          setSelectedRowId(res.data.winner?.id);
          setPage(res.data.existOnPage);
          message.success(t('message.random.success'));
        }
      })
      .catch(e => {
        message.error(e.errorMessage || t('message.random.fail'));
        setIsPickingRandomWinner(false);
      });
  }, [tourTrash]);

  const handleChangePage = useCallback((current: number, size?: number) => {
    setPage(current);
    setPageSize(size || PER_PAGE_DEFAULT);
  }, []);

  const handleSelectRow = useCallback((record: TourTrashUserType) => {
    if (props.tourTrash?.status === TourTrashStatusType.ACTIVE) {
      setSelectedRowId(record.id);
    }
  }, []);

  const canChooseWinner =
    tourTrash?.status === TourTrashStatusType.ACTIVE && moment().isAfter(moment(tourTrash.endDate));

  return (
    <StyledUsersContainer>
      <Row gutter={32}>
        <Col span={12}>
          <StyledChooseWinnerRandomlyButton
            onClick={handlePickRandomWinner}
            disabled={!canChooseWinner || isPickingRandomWinner}
            loading={isPickingRandomWinner}
            size="large"
            block
          >
            {!isPickingRandomWinner && <img src={RandomIcon} alt="" />}
            {t('tab.player.random')}
          </StyledChooseWinnerRandomlyButton>
        </Col>
        <Col span={12}>
          <span>{t('tab.player.search')}</span>
          <StyledSearchInput
            onChange={e => setSearch(e.target.value)}
            value={search}
            placeholder={t('tab.player.placeholder')}
            suffix={!search ? <SearchOutlined /> : <CloseOutlined onClick={() => setSearch('')} />}
          />
        </Col>
      </Row>
      <UserList
        perPage={pageSize}
        page={page}
        handlePickWinner={handlePickWinner}
        pickingWinner={isPickingWinner}
        tourTrash={tourTrash}
        q={debouncedSearch}
        handleChangePage={handleChangePage}
        handleSelectRow={handleSelectRow}
        selectedRowId={selectedRowId || (tourTrash && tourTrash.winner && tourTrash.winner.id)}
        disableTab={disableTab}
      />
    </StyledUsersContainer>
  );
};
